
import Vue from 'vue';
import { Component, Inject, Ref, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { FilterBar } from '@/lazy-load-components';
const NewProBanner = () => import('@/components/NewProBanner.vue');

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import TitleTimelineGraphql from '@/components/timeline/TitleTimelineGraphql.vue';

import { CollectionType } from '@/enums/collection-type';
import { WebLocale } from '@/enums/web-locale';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';
import {
	PageNavigationTransaction,
	sentryPerformanceTracker,
} from '@/helpers/tracking/performance/SentryPerformanceTracker';
import {
	GetNewTitleBucketsDocument,
	GetNewTitleBucketsQueryVariables,
} from '@/pages/graphql/queries/GetNewTitleBuckets.query';

import type { BucketData } from '@/interfaces/new-title-graphql';
import type { FilterCollection } from '@/stores/filter.store';
import type { UserSettings } from '@/stores/user.store';

import { NewPageType, ObjectType, TitleFilter } from '@/@types/graphql-types';
import { getActiveFilters } from '@/helpers/filter-helper';
import { toastError } from '@/helpers/toast/toast-helper';
import type { Content } from '@/interfaces/components/content';
import type { FormattedTimelineData, NewPageBucketsData } from '@/interfaces/new-title-graphql';

const NewPageTypeSwitcher = () =>
	import(/* webpackChunkName: "pricedrop-switcher" */ '@/components/nav/switcher/NewPageTypeSwitcher.vue');
const NewPageTypeSwitcherSeoFriendly = () =>
	import(/* webpackChunkName: "pricedrop-switcher" */ '@/components/nav/switcher/NewPageTypeSwitcherSeoFriendly.vue');
const FallbackTitles = () =>
	import(/* webpackChunkName: "fallback-titles" */ '@/components/timeline/FallbackTitles.vue');

const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

type GroupedItems = {
	[key: string]: BucketData[];
};

function groupBy(items: BucketData[], key: string): GroupedItems | null {
	return items?.reduce((acc, item) => {
		const groupValue: string | undefined = key.includes('bucketType') ? item.key.bucketType : item.key.date;
		if (!groupValue) return acc;
		if (!acc[groupValue]) {
			acc[groupValue] = [];
		}
		acc[groupValue].push(item);
		return acc;
	}, {} as GroupedItems);
}
const initialRowsByCountry: Partial<Record<WebLocale, number>> = { tr: 7, it: 6, mx: 8 };
@Component<New>({
	components: {
		NewProBanner,
		TitleTimelineGraphql,
		NewPageTypeSwitcher,
		NewPageTypeSwitcherSeoFriendly,
		ContentHeader,
		FilterBar,
		FallbackTitles,
	},
	name: 'New',
	apollo: {
		newPageData: {
			query: GetNewTitleBucketsDocument,
			variables(): GetNewTitleBucketsQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				this.isFirstPageLoaded[this.newPageType] = true;
				return data.newTitleBuckets;
			},
			skip() {
				return !this.newPageType;
			},
			error() {
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class New extends Vue {
	newPageData: NewPageBucketsData = {} as NewPageBucketsData;
	allNewTitles = [];
	showFooter = false;
	isFirstPageLoaded: Record<NewPageType, boolean> = {
		[NewPageType.New]: false,
		[NewPageType.LeavingSoon]: false,
		[NewPageType.Upcoming]: false,
		[NewPageType.PriceDrop]: false,
	};
	CollectionType = CollectionType;

	@Filter.Getter(CollectionType.NEW) filtersNew: FilterCollection;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.Getter activeCollectionType: CollectionType;
	@Routing.State activeRoute: any;

	@User.State settings: UserSettings;
	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.Getter isPremium: boolean;

	@Inject('routeParams') routeParams: (collectionType: CollectionType) => any;

	@Ref('content') readonly content!: Content;
	@Ref('titleTimeline') readonly titleTimeline!: TitleTimelineGraphql;

	@Watch('allNewTitles')
	onAllTitlesPassed() {
		this.$store.dispatch('meta/setNewPageJsonLD', this.allNewTitles);
	}

	get queryVariables() {
		const initialRowsNumber = initialRowsByCountry[this.webLocale] ?? 5;
		return {
			country: this.country,
			newAfterCursor: '',
			newTitlesFilter: {
				...this.graphqlFilters,
				objectTypes: this.filterObjectTypes,
			},
			priceDrops: this.isPriceDropView,
			bucketSize: 0,
			first: this.isLeavingOrComingSoon && !this.isPremium ? 1 : initialRowsNumber,
			pageType: this.newPageType,
		};
	}

	get isLeavingOrComingSoon() {
		return this.newPageType === NewPageType.LeavingSoon || this.newPageType === NewPageType.Upcoming;
	}

	get showNewProBanner() {
		return !this.isPremium && this.isLeavingOrComingSoon && !this.isLoading;
	}

	get isPriceDropView() {
		return this.newPageType === NewPageType.PriceDrop;
	}

	get newPageType() {
		return this.$route?.meta?.newPageType as NewPageType;
	}

	get formattedTimelineData(): FormattedTimelineData[] {
		const groupByProperty = this.isLeavingOrComingSoon ? 'key.bucketType' : 'key.date';
		const groupedBuckets = groupBy(this.newPageData.edges, groupByProperty);
		if (!groupedBuckets) return [];
		return Object.keys(groupedBuckets ?? '').reduce((newPageDays, key, currentIndex) => {
			/*
       Due to a race condition when changing between Leaving soon/Coming soon and New/Pricedrops
       groupBy() produces an object with a property "undefined" (as a string, not a type), that then used as a key for buckets.
       It causes some visual artifacts and wrong GraphQL requests when switch between pages. This hack prevents it.
      */
			if (key === 'undefined') {
				return [];
			}

			/*
			 * Show only first bucket to non pro users
			 * */
			if (!this.isPremium && currentIndex > 0 && this.isLeavingOrComingSoon) {
				return newPageDays;
			}

			return [
				...newPageDays,
				{
					key,
					isGroupedByDate: groupByProperty !== 'key.bucketType',
					providers:
						this.isLeavingOrComingSoon && !this.isPremium
							? groupedBuckets[key].slice(0, 1)
							: groupedBuckets[key],
				},
			];
		}, [] as FormattedTimelineData[]);
	}

	get showSpinner() {
		return !this.isFirstPageLoaded[this.newPageType] && this.isLoading;
	}

	// We need this mapper due to filter naming on the New Page
	// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
	get filterObjectTypes() {
		return this.graphqlFilters?.objectTypes?.map(objectType =>
			objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
		);
	}

	get showFallbackTitles() {
		const activeFilters = getActiveFilters(Object.keys(this.filtersNew) as any, this.filtersNew);

		return (
			activeFilters.content_types &&
			activeFilters.providers &&
			Object.keys(activeFilters).length === 2 &&
			(this.newPageData.edges ?? []).length === 0
		);
	}

	@Watch('isTitlesLoading')
	onIsTitlesLoading(isTitlesLoading: boolean) {
		if (isTitlesLoading) return;
		sentryPerformanceTracker.finishTransaction(PageNavigationTransaction.New);
	}

	@Watch('filtersNew')
	changedFilters() {
		this.isFirstPageLoaded[this.newPageType] = false;

		this.titleTimeline?.resetInfiniteScroll();

		// scroll to top if the filters have changed
		this.scrollToTop();
	}

	get isLoading() {
		return this.$apollo.queries.newPageData.loading;
	}

	get isFirstPage() {
		return !this.newPageData?.pageInfo?.hasPreviousPage;
	}

	get isAtEndOfList() {
		return !this.newPageData?.pageInfo?.hasNextPage || this.showNewProBanner;
	}

	get graphqlFilters(): TitleFilter {
		const selectedProviders =
			this.filtersNew.providers?.length === 0 ? this.settings.providers : this.filtersNew.providers;
		return toGraphqlFilters(this.filtersNew, selectedProviders, this.isHideSeenDislikedTitlesEnabled);
	}

	get hasSeoFriendlyAppHeader() {
		return this.webLocale === WebLocale.ec;
	}

	mounted() {}

	scrollToTop() {
		this.content?.scrollToTop();
	}

	trackClick(payload: TitleEventPayloadGraphql) {
		trackTitleEventGraphql('title_clicked', payload, 'new-timeline');
	}

	showErrorToast() {
		toastError({ message: 'Something went wrong, please try again' });
	}

	onLoadMore({ event }: { event: any }) {
		event &&
			this.$apollo.queries.newPageData
				.fetchMore({
					variables: {
						newAfterCursor: this.newPageData.pageInfo.endCursor,
					},
					updateQuery(previousResult, { fetchMoreResult }) {
						return {
							newTitleBuckets: {
								__typename: previousResult.newTitleBuckets.__typename,
								edges: [
									...previousResult.newTitleBuckets.edges,
									...fetchMoreResult.newTitleBuckets.edges,
								],
								pageInfo: fetchMoreResult.newTitleBuckets.pageInfo,
							},
						};
					},
				})
				.catch(e => {
					captureMessageForSentry(
						'[GraphQL New Page Load More Buckets Issue]:',
						{ error: e, where: 'Component: New, method: onLoadMore' },
						'error'
					);
				})
				.finally(() => event.target && event.target.complete(this.newPageData.edges?.length));
	}
}
